import type { DesktopAgent } from '@finos/fdc3';
import type {
  AgentConfig,
  AgentConfigHandlerInfo,
  AgentConfigInfo,
} from './types';
import type { ConnectifiFab } from './fab';
export type { IntentContextGroup } from '@/common/apps';

import { ConnectifiAgent } from './agent';
import { registerIframePort } from './agent/iframe';
import { addToDocumentBody, hideElement } from './common/element';
import { parseAppId } from './common/app-id';
import { addSandboxAttributes } from './common/iframe';
export {
  InteropStrategies,
  ConnectifiAppMetadata,
  IntentResultType,
} from '@/common/types';

export {
  AgentConfig,
  AppIntentResult,
  FabProps,
  ResolveCallback,
  CloseCallback,
  ResolveCallbackProps,
  IntentResolutionMessage,
  ResolutionType,
  AuthenticateConfig,
  RequestStorageAccessConfig,
  ValidPositions,
  ConnectifiOpenMessage,
  DirectoryProps,
  LogLevel,
} from './types';

export const createAgent = async (
  interopHost: string,
  appId: string,
  config?: AgentConfig,
): Promise<DesktopAgent | undefined> => {
  if (!appId || appId.length === 0 || appId.indexOf('@') < 1) {
    throw new Error('missing or malformed appId');
  }
  if (!interopHost || interopHost.length === 0) {
    throw new Error('invalid interop host');
  }

  const interopUrl = new URL(interopHost);
  interopHost = `${interopUrl.protocol}//${interopUrl.host}`;

  const configInfo = configToInfo(config);

  const root = document.createElement('div');
  await addToDocumentBody(root);

  const iframe = document.createElement('iframe');
  addSandboxAttributes(iframe);
  hideElement(iframe);
  root.appendChild(iframe);

  const port = await registerIframePort(iframe, interopHost, appId);

  const { directory, namespace, appName } = parseAppId(appId);

  let wrappedConfig = config;
  let fab: ConnectifiFab | undefined;
  const isFabEnabled = !config?.headless;
  if (isFabEnabled) {
    // Import dynamically so esbuild can code split fab.
    const { wrapConfig, ConnectifiFab } = await import('./fab');
    fab = new ConnectifiFab(interopHost, root, config?.props);
    wrappedConfig = wrapConfig(fab, config || {});
  }

  const agent = new ConnectifiAgent(
    interopHost,
    appName,
    namespace,
    directory,
    port,
    wrappedConfig,
    configInfo,
  );
  if (fab) {
    fab.bind(agent); // FAB probably shouldn't need agent
  }

  return agent.start();
};

const configToInfo = (
  config: AgentConfig | undefined,
): AgentConfigInfo | undefined => {
  if (!config) {
    return undefined;
  }
  const { logLevel, props } = config;
  const activeHandlers = Object.entries(config).reduce(
    (result, [key, value]) => {
      if (
        (key.startsWith('on') || key.startsWith('handle')) &&
        value !== undefined
      ) {
        result[key as keyof AgentConfigHandlerInfo] = true;
      }
      return result;
    },
    {} as AgentConfigHandlerInfo,
  );
  return {
    bridgeGlobal: config.bridgeGlobal === true,
    headless: config.headless === true,
    logLevel,
    props,
    activeHandlers,
  };
};
